import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as actions from './actionCreators';
import { Dropdown, PasswordInput, TextInput } from '../components';
import * as JspUtil from '../common/JspUtil';
import { jspGlobals } from '../common/jspGlobals';
import ReactCodeInput from 'react-code-input';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
    EMAIL_DELIVERY,
    GOOGLE_AUTH_DELIVERY,
    WEBAUTHN_DELIVERY
} from './actionCreators';
import TFAHelp from './TFAHelp';
import { getLabelFromJspVariables } from '../common/JspUtil';
import { apiPostAsync } from '../common/service-utils';
import * as errorCodes from '../common/errorCodes';
import { useJspVariables, useAppContext, useLoginFlowState, useLoginInput, useDebugRender, useLoader } from '../hooks';

const linkSx = { color: '#000', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', textDecorationColor: '#000' };

const getAlertSeverity = (resp) => {
    const code = Number(resp?.code);
    const isChoiceOffered =
        code === errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT ||
        code === errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT_NOT_VERIFIED ||
        code === errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_VERIFIED ||
        code === errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_NOT_ENABLED;
    const attempts = resp?.data?.remainingAppIdLoginAttempts;
    const attemptsRemain = attempts != null && attempts !== '' && Number(attempts) > 0;
    return isChoiceOffered && attemptsRemain ? 'warning' : 'error';
};

const parseLdapDomains = (value) => {
    if (!value) return [];
    try {
        let domains = Array.isArray(value) ? value : JSON.parse(value);
        if (typeof domains === 'string') domains = JSON.parse(domains);
        return Array.isArray(domains)
            ? domains.filter(domain => typeof domain === 'string' && domain.trim())
                .map(domain => domain.trim())
            : [];
    } catch (e) {
        return [];
    }
};

const findSelectedLdapDomain = (userName, domains) => {
    if (!userName) return domains[0] || '';
    const slashIndex = userName.indexOf('\\');
    const atIndex = userName.lastIndexOf('@');
    const qualifiedDomain = slashIndex > 0
        ? userName.substring(0, slashIndex)
        : (atIndex > 0 && atIndex < userName.length - 1 ? userName.substring(atIndex + 1) : '');
    return domains.find(domain => domain.toLowerCase() === qualifiedDomain.toLowerCase()) || domains[0] || '';
};

const qualifyLdapUserName = (userName, domain) => {
    if (!userName || !domain || userName.includes('\\') || userName.includes('@')) return userName;
    return `${domain}\\${userName}`;
};

const TfaOptionCard = ({ label, name, isRegistered, onUse, registerLabel, onRegister, showRegister, children, dataAutomationId }) => (
    <Box sx={{ flex: '1 1 50%', minWidth: { xs: '100%', sm: 0 } }}>
        <Button variant="outlined" fullWidth type="button" name={name}
            disabled={!isRegistered}
            onClick={onUse}
            data-automation-id={dataAutomationId}>
            {label}
        </Button>
        {!isRegistered && showRegister &&
            <Link component="button" type="button" variant="caption" onClick={onRegister}
                data-automation-id={dataAutomationId ? `${dataAutomationId}_register_link` : undefined}
                sx={{ display: 'block', mt: 0.5, textAlign: 'center', color: '#00a800', fontSize: '0.75rem' }}>
                {registerLabel}
            </Link>
        }
        {children}
    </Box>
);

const LoginTemplate = ({appUserLoginTfa, onTfaOtpChange, onLoginBtnClick, onFormSubmit }) => {
    useDebugRender('LoginTemplate', { appUserLoginTfa, onFormSubmit: !!onFormSubmit });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const usernameInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const tfaPasscodeInputRef = useRef(null);

    const focusFirstError = (fieldErrors) => {
        if (fieldErrors.username) usernameInputRef.current?.focus();
        else if (fieldErrors.password) passwordInputRef.current?.focus();
        else if (fieldErrors.tfaPasscode) tfaPasscodeInputRef.current?.focus();
    };
    const { jspVariables } = useJspVariables();
    const { showLoader, hideLoader } = useLoader();
    // appKey is Redux-only (bootstrapped in layout/actionCreators). Keeping a local
    // copy here previously let it go stale after logout blanked the JSP globals.
    const { appKey, getLoginApp, getApp, onAppNameChange, getLabelFromJspVariables: getLabelHook } = useAppContext(jspVariables);
    const { userName: contextUserName, setUserName: setContextUserName } = useLoginInput();

    const loginAppsList = useSelector(state => state.globalReducer.loginAppsList);

    // All form inputs — local state, not Redux
    // Initialize from JSP globals (server-set) first, then context (SPA navigation)
    const [userName, setUserNameLocal] = useState(() => {
        if (JspUtil.isValueAvailable(window.jspIceSsoGid)) return window.jspIceSsoGid;
        if (JspUtil.isValueAvailable(window.jspReloginUserId)) return window.jspReloginUserId;
        if (jspVariables.jspLoginHint && JspUtil.isValueAvailable(jspVariables.jspLoginHint)) return jspVariables.jspLoginHint;
        if (jspVariables.jspUserId && JspUtil.isValueAvailable(jspVariables.jspUserId)) return jspVariables.jspUserId;
        return contextUserName || '';
    });
    const setUserName = (val) => {
        setUserNameLocal(val);
        setContextUserName(val);
    };
    const [ldapDomains] = useState(() => parseLdapDomains(jspVariables.jspLdapDomains));
    const ldapDomainOptions = ldapDomains.map(domain => ({ domain, description: domain }));
    const [selectedLdapDomain, setSelectedLdapDomain] = useState(
        () => findSelectedLdapDomain(userName, ldapDomains)
    );
    const loginUserName = qualifyLdapUserName(userName, selectedLdapDomain);
    const [password, setPassword] = useState('');
    const [passwordLessOtp, setPasswordLessOtp] = useState('');
    const passwordUrl = JspUtil.isValueAvailable(jspVariables.jspPasswordUrl)
        ? jspVariables.jspPasswordUrl.trim()
        : '';
    const [tfaPasscode, setTfaPasscode] = useState('');
    const mersOrganization = '';
    const [rememberMe, setRememberMe] = useState(() => JspUtil.isValueAvailable(jspVariables.jspIceSsoGid));
    // Set by authenticateUser error handler / PageAppUserLoginTfa via Redux.
    const tfaDeliveryPreference = useSelector(state => state.userInputsReducer.tfaDeliveryPreference);
    const {
        SAVED_AUTH_PARAM,
        ERR_REQUIRES_TFA: ERR_REQUIRES_TFA_VAL,
        TFA_USE_OVERRIDE: TFA_USE_OVERRIDE_VAL,
        TFA_IS_WEBAUTHN_REGISTERED,
        TFA_IS_TFA_DEVICE_REGISTERED,
        ERR_REQUIRES_PASSWORD_LESS_OTP,
        ERR_REQUIRES_PASSWORD: ERR_REQUIRES_PASSWORD_VAL,
        tfaPrompt,
    } = useLoginFlowState();
    const {
        errTransition,
        errNoGlobal,
        errAttemptsExceeded,
        remainingAttempts,
        email: transGlobalIdEmail,
    } = useSelector(state => state.authenticateReducer.globalIdTransition);
    const eulaDoc = useSelector(state => state.authenticateReducer.eulaState.eulaDoc);

    const [samlUrl] = useState(() => {
        return JspUtil.isValueAvailable(window.jspSamlAuthUrl) ? window.jspSamlAuthUrl : '';
    });
    const [showAppSelector, setShowAppSelector] = useState(true);
    const [show2faHelp, setShow2faHelp] = useState(false);
    const [errors, setErrors] = useState({});
    const [loginMode, setLoginMode] = useState(null); // null = not determined, 'password' | 'passwordless_otp' | 'passwordless_saml'
    // The server offers the passcode without emailing it; the code is sent only when
    // the user asks for it, so the offer and the 8-digit screen are separate states.
    const [isPasscodeFlow, setIsPasscodeFlow] = useState(false);
    // Set when the user picks password over the offered passcode.
    const [passwordLoginChosen, setPasswordLoginChosen] = useState(false);
    const [sendingPasscode, setSendingPasscode] = useState(false);
    // Set once the user opts into Global ID login from the transition prompt. The
    // field then holds an email, so it must stay labelled as one after the
    // transition flags are cleared.
    const [useGlobalIdLogin, setUseGlobalIdLogin] = useState(false);

    const [alert, setAlert] = useState(null);
    const [authenticating, setAuthenticating] = useState(false);

    useEffect(() => {
        // Clear stale TFA state from previous login attempts
        if (!appUserLoginTfa) {
            dispatch({ type: actions.TFA_STATE, data: { required: false, useOverride: '', prompt: '', webAuthnRequest: '' } });
        }

        if (JspUtil.isValueAvailable(jspVariables.jspApp) && !appKey) {
            onAppNameChange(jspVariables.jspApp);
        }
        if (usernameInputRef.current) usernameInputRef.current.focus();
        if (jspVariables.showRememberMe) {
            if (JspUtil.isValueAvailable(jspVariables.jspIceSsoGid)) {
                passwordInputRef.current && passwordInputRef.current.focus && passwordInputRef.current.focus();
            }
        }
        if (JspUtil.isValueAvailable(window.jspReloginUserId)) {
            // User ID is prefilled but stays editable — focus the password field.
            passwordInputRef.current && passwordInputRef.current.focus && passwordInputRef.current.focus();
        }
        let showApp = jspVariables.isAppDropDownVisible && (!JspUtil.isValueAvailable(jspVariables.jspLoginApp));
        if (showApp) {
            let app = getLoginApp();
            if (typeof app !== 'undefined' && app) {
                showApp = false;
            } else {
                showApp = false;
                onAppNameChange('ANY');
            }
        }
        setShowAppSelector(showApp);

        if (appUserLoginTfa) {
            dispatch({ type: actions.TFA_STATE, data: { required: true } });
        }

        if (JspUtil.isValueAvailable(jspVariables.jspDisplayPasswordField) && jspVariables.jspDisplayPasswordField === 'Y') {
            onNextClick();
        }
    }, []);

    // MFA page loads tfaEmail after mount; copy it into the disabled email field.
    useEffect(() => {
        if (contextUserName && !userName) {
            setUserNameLocal(contextUserName);
        }
    }, [contextUserName, userName]);

    // App-ID attempts exhausted: the server returns the Global ID email, and login
    // must continue with it. Prefill the field so the user only enters a password.
    useEffect(() => {
        if (errAttemptsExceeded && transGlobalIdEmail) {
            setUserName(transGlobalIdEmail);
            setPassword('');
        }
    }, [errAttemptsExceeded, transGlobalIdEmail]);

    const onPasswordChange = (event) => {
        if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
        setPassword(event.target.value);
    };

    const onPasswordLessOtpChange = (value) => {
        setPasswordLessOtp(value);
    };

    const onRememberUserNameChange = (event) => {
        setRememberMe(event.target.checked);
    };

    const onTfaPasscodeChange = (event) => {
        setTfaPasscode(event.target.value);
        if (onTfaOtpChange) onTfaOtpChange(event);
    };

    const redirectToSaml = (e, url) => {
        e.preventDefault();
        const saml = JSON.parse(url);
        if (saml) {
            if (saml.postUrl) {
                postSaml(saml);
            } else if (saml.redirectUrl) {
                location.href = saml.redirectUrl;
            }
        }
    };

    const postSaml = (saml) => {
        let form = document.createElement('form');
        form.method = 'POST';
        form.action = saml.postUrl;
        let base64EncodedRelayState = saml.relayState;
        if (base64EncodedRelayState && base64EncodedRelayState.length > 0) {
            let relayState = document.createElement('input');
            relayState.type = 'hidden';
            relayState.name = "RelayState";
            relayState.value = atob(base64EncodedRelayState);
            form.appendChild(relayState);
        }
        let response = document.createElement('input');
        response.type = 'hidden';
        response.name = "SAMLRequest";
        response.value = saml.requestBody;
        form.appendChild(response);
        document.body.appendChild(form);
        form.submit();
    };

    const continueLoginWithAppId = (event) => {
        dispatch({ type: actions.GLOBAL_ID_TRANSITION, data: actions.GLOBAL_ID_TRANSITION_CLEAR });
        dispatch({ type: actions.CONTINUE_WITH_APPID_CLICK, data: 'Y' });
        authenticate(event, 'Y');
    };

    const loginWithGlobalId = () => {
        dispatch({ type: actions.GLOBAL_ID_TRANSITION, data: { errTransition: false } });
        dispatch({ type: actions.USER_INPUT_USER_NAME_CHANGE, data: transGlobalIdEmail || '' });
        dispatch({ type: actions.USER_INPUT_PASSWORD_CHANGE, data: '' });
        // The username/password inputs are local state, so the dispatches above do
        // not reach them — set the Global ID email on the field directly.
        setUserName(transGlobalIdEmail || '');
        setPassword('');
        setUseGlobalIdLogin(true);
        setErrors({});
        setAlert(null);
    };

    const registerGlobalId = () => {
        navigate(constants.ROUTE_PAGE_REQUEST_GLOBAL_ID);
    };

    const onTfaDeliveryPreferenceOptionChange = (value) => {
        setTfaPasscode('');
        const savedParam = SAVED_AUTH_PARAM;
        dispatch({ type: actions.TFA_STATE, data: { useOverride: value } });
        setAlert(null);
        if (value === WEBAUTHN_DELIVERY) {
            dispatch({ type: actions.TFA_STATE, data: { prompt: 'Follow screen instructions to continue with Passkey (Yubikey/Fingerprint) login' } });
            dispatch(actions.authenticateUser(
                savedParam.userName, savedParam.appKey, savedParam.loginApp, savedParam.password,
                savedParam.passwordLessOtp, savedParam.captchaResponse, '', savedParam.mersOrganization,
                savedParam.rememberMe, null, 'Y', savedParam.appUserLoginTfa));
        } else if (value === GOOGLE_AUTH_DELIVERY) {
            dispatch({ type: actions.TFA_STATE, data: { prompt: 'Please enter the code from your Authenticator app' } });
        } else if (value === EMAIL_DELIVERY) {
            actions.authAndSendCodeTo2FAEmail(
                savedParam.userName, savedParam.appKey, savedParam.password,
                { tfaPasscode: savedParam.tfaPasscode, mersOrganization: savedParam.mersOrganization, rememberMe: savedParam.rememberMe, appUserLoginTfa: savedParam.appUserLoginTfa, loginApp: savedParam.loginApp }
            ).then(() => {
                dispatch({ type: actions.TFA_STATE, data: { required: true, prompt: 'Enter 2FA Passcode sent to your email to continue with Login' } });
            }).catch((resp) => {
                dispatch({ type: actions.TFA_STATE, data: { required: false, useOverride: '', prompt: '' } });
                setAlert({ message: resp.message || getLabelFromJspVariables('err_send_2fa_failed'), severity: 'error' });
            });
        } else {
            dispatch({ type: actions.TFA_STATE, data: { prompt: '' } });
        }
    };

    const editTfaSettings = (actionName) => {
        dispatch({ type: actions.TFA_STATE, data: { required: false } });
        if (actionName) dispatch({ type: actions.REGISTER_ACTION, data: actionName });
        if (jspVariables.jspLoginApp === constants.APP_LAUNCHER) {
            const win = window.open(origin + "/index.html#" + constants.ROUTE_PAGE_TFA_REGISTER, '_blank', 'noopener,noreferrer');
            if (win) win.opener = null;
            win?.focus();
        } else {
            navigate(constants.ROUTE_PAGE_TFA_REGISTER);
        }
    };

    // `continueWithAppId` is passed explicitly rather than read from Redux: the
    // dispatch in continueLoginWithAppId has not been applied yet when this runs.
    const authenticate = useCallback(async (event, continueWithAppId = 'N') => {
        if (event) event.preventDefault();
        if (authenticating) return;
        const fieldErrors = {};
        if (!appUserLoginTfa && !userName?.trim()) fieldErrors.username = errorCodes.MSG_FIELD_REQUIRED();
        const isPasswordLessOtpLogin = loginMode === 'passwordless_otp' || (ERR_REQUIRES_PASSWORD_LESS_OTP && !isPasscodeFlow);
        if (!appUserLoginTfa && !isPasswordLessOtpLogin && !password?.trim()) fieldErrors.password = errorCodes.MSG_FIELD_REQUIRED();
        if (!appUserLoginTfa && isPasswordLessOtpLogin && !passwordLessOtp?.trim()) fieldErrors.password = errorCodes.MSG_FIELD_REQUIRED();
        if ((ERR_REQUIRES_TFA_VAL || appUserLoginTfa) && !tfaPasscode?.trim()) fieldErrors.tfaPasscode = errorCodes.MSG_FIELD_REQUIRED();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            focusFirstError(fieldErrors);
            return;
        }
        setErrors({});
        setAlert(null);
        if (appUserLoginTfa) {
            onLoginBtnClick(event);
            return;
        }
        setAuthenticating(true);
        showLoader('Authenticating...');
        // Derive the app at call time: getApp() reads the live JSP globals and falls
        // back to Redux (which defaults to ANY), so it stays correct after a logout
        // has blanked the globals.
        const effectiveAppKey = getApp();
        const authParam = actions.saveAuthParam(loginUserName, effectiveAppKey, getLoginApp(), password, passwordLessOtp, '', tfaPasscode, mersOrganization, rememberMe, null, null, appUserLoginTfa);
        dispatch({ type: actions.SAVED_AUTH_PARAM, data: authParam });
        dispatch({ type: actions.AUTH_SESSION, data: { isAuthenticated: false } });
        const startTime = Date.now();
        const onAuthSuccess = (resp, isLauncher) => {
            setPasswordLessOtp('');
            dispatch({ type: actions.AUTH_SESSION, data: {
                authenticatedUser: { authStart: startTime, authEnd: Date.now(), ...resp.data },
                isAuthenticated: true,
                authenticatedApp: resp.data.authenticatedApp,
                isLauncherAuth: !!isLauncher,
            }});
            if (resp.data.userMessage) {
                setAlert({ message: resp.data.userMessage, severity: 'success' });
            }
            if (resp.data.addressStatus === 'P') {
                navigate(constants.ROUTE_PAGE_CONFIRM_CONTACT, { state: { sourcePage: constants.ROUTE_PAGE_AUTHENTICATED } });
                return;
            }
            dispatch({ type: actions.TFA_STATE, data: { required: false } });
            dispatch({ type: actions.GLOBAL_ID_TRANSITION, data: actions.GLOBAL_ID_TRANSITION_CLEAR });
            setAuthenticating(false);
            navigate(constants.ROUTE_PAGE_AUTHENTICATED);
        };
        const onAuthFailure = (resp, effectiveAppKey) => {
            setAuthenticating(false);
            hideLoader();
            setPasswordLessOtp('');
            if (typeof resp.code == 'undefined') {
                setAlert({ message: getLabelFromJspVariables('err_login_failed'), severity: 'error' });
                return;
            }
            dispatch({ type: actions.USER_INPUT_TFA_PASSCODE_CHANGE, data: '' });
            const errorMsg = actions.handleAuthError(dispatch, resp, {
                password, permissions: effectiveAppKey, postData: { userId: loginUserName, appKey: effectiveAppKey }, authParam,
                passwordLessOtp, captchaResponse: '', tfaPasscode
            });
            if (errorMsg) setAlert({ message: errorMsg, severity: getAlertSeverity(resp) });
        };

        try {
            const { resp, isLauncher } = await actions.authenticateUserAsync({
                userName: loginUserName, appKey: effectiveAppKey, loginApp: getLoginApp(), password, passwordLessOtp, captchaResponse: '',
                tfaPasscode, mersOrganization, rememberMe, appUserLoginTfa,
                continueLoginWithAppId: continueWithAppId,
            });
            onAuthSuccess(resp, isLauncher);
        } catch (resp) {
            // iceIM fallback: an ICE-app login that fails on bad credentials / lockout
            // retries once against the YJ app. Run it through the same async helpers so
            // a second failure still surfaces an alert (previously the re-dispatched
            // thunk swallowed the error, leaving the user with no feedback).
            const isIceImFallback = JspUtil.isValueAvailable(window.jspPermissions)
                && window.jspPermissions === 'iceIM' && effectiveAppKey === 'ICE'
                && (resp.code == errorCodes.ERR_GENERAL || resp.code == errorCodes.ERR_ACCOUNT_LOCKED);
            if (isIceImFallback) {
                dispatch({ type: actions.USER_INPUT_APP_KEY_CHANGE, data: 'YJ' });
                try {
                    const { resp: yjResp, isLauncher: yjIsLauncher } = await actions.authenticateUserAsync({
                        userName: loginUserName, appKey: 'YJ', loginApp: 'iceIM', password, passwordLessOtp, captchaResponse: '',
                        tfaPasscode, mersOrganization, rememberMe, appUserLoginTfa,
                        continueLoginWithAppId: continueWithAppId,
                    });
                    onAuthSuccess(yjResp, yjIsLauncher);
                } catch (yjResp) {
                    onAuthFailure(yjResp, 'YJ');
                }
                return;
            }
            onAuthFailure(resp, effectiveAppKey);
        }
    }, [userName, loginUserName, password, passwordLessOtp, tfaPasscode, mersOrganization, rememberMe, appUserLoginTfa, ERR_REQUIRES_PASSWORD_VAL, ERR_REQUIRES_TFA_VAL, ERR_REQUIRES_PASSWORD_LESS_OTP, loginMode, isPasscodeFlow, authenticating, getApp, getLoginApp]);

    const sendPasswordLessOtpEmail = async (postData) => {
        try {
            await apiPostAsync(constants.API_SEND_PASSWORD_LESS_OTP, null, postData);
        } catch {
            // A successful send comes back as REQUIRES_PASSWORD_LESS_OTP (-36200),
            // which apiPostAsync rejects. SAML is decided on Next.
        }
    };

    const onUserNameChange = (e) => {
        if (errors.username) setErrors(prev => ({ ...prev, username: '' }));
        const nextUserName = e.target.value;
        setUserName(nextUserName);
        // Next already classified the previous email. A different username has to
        // go through Next again, which is where SAML vs passcode is decided.
        if (nextUserName !== userName && (loginMode != null || isPasscodeFlow || passwordLoginChosen)) {
            setLoginMode(null);
            setIsPasscodeFlow(false);
            setPasswordLoginChosen(false);
            setPassword('');
            setPasswordLessOtp('');
            setSendingPasscode(false);
        }
    };

    const onNextClick = async () => {
        if (!userName?.trim()) {
            setErrors({ username: errorCodes.MSG_FIELD_REQUIRED() });
            usernameInputRef.current?.focus();
            return;
        }
        setErrors({});
        const globalId = loginUserName || jspVariables.jspLoginHint;
        if (!globalId || globalId.length < 3) return;

        const postData = { globalId, appKey: getLoginApp(), rememberUserId: rememberMe ? 'Y' : 'N' };
        if (JspUtil.isValueAvailable(window.jspPermissions)) {
            postData.permissions = window.jspPermissions;
        }
        try {
            await apiPostAsync(constants.API_GET_SAML_CONFIG_BY_EMAIL_DOMAIN, null, postData);
            // Success (code 0) — password login
            setLoginMode('password');
        } catch (resp) {
            // The server may send `code` as a string (e.g. "-36100"); coerce to
            // Number so it matches the numeric error-code constants. A strict ===
            // against a string silently fell through to the password branch, which
            // bypassed the passwordless SAML redirect (SSO-5358 regression).
            const code = Number(resp.code);
            if (code === errorCodes.ERR_PASSWORD_LESS_REQUIRES_SAML_REDIRECT) {
                dispatch({ type: actions.PASSWORD_LESS_SAML_CONFIG, data: resp.data });
                setLoginMode('passwordless_saml');
                navigate(constants.ROUTE_PAGE_PASSWORD_LESS_SAML_SUBMIT);
            } else if (code === errorCodes.ERR_PASSWORD_LESS_OTP_FLOW
                    || code === errorCodes.ERR_PASSWORD_LESS_REQUIRES_OTP) {
                setIsPasscodeFlow(true);
                setLoginMode('password');
            } else {
                // Any other error - default to password login
                setLoginMode('password');
            }
        }
    };

    // A remembered user skips Next, so the server tells us up front that the passcode
    // is available. Nothing has been emailed yet, so show the offer rather than the code input.
    useEffect(() => {
        if (jspVariables.jspPasswordLessEnabled && jspVariables.jspPasswordLessPreferred) {
            setIsPasscodeFlow(true);
            setLoginMode('password');
        }
    }, []);

    const onSendOneTimePasscodeClick = async () => {
        // The code rotates every 30s, so a second send emails a different passcode.
        if (sendingPasscode) return;
        const globalId = userName || jspVariables.jspLoginHint;
        if (!globalId?.trim()) {
            setErrors({ username: errorCodes.MSG_FIELD_REQUIRED() });
            usernameInputRef.current?.focus();
            return;
        }
        setErrors({});
        setAlert(null);
        setPassword('');
        const postData = { globalId, appKey: getLoginApp(), rememberUserId: rememberMe ? 'Y' : 'N' };
        if (JspUtil.isValueAvailable(window.jspPermissions)) {
            postData.permissions = window.jspPermissions;
        }
        setSendingPasscode(true);
        setLoginMode('passwordless_otp');
        try {
            await sendPasswordLessOtpEmail(postData);
        } finally {
            setSendingPasscode(false);
        }
    };

    const getFormValues = () => ({ userName: loginUserName, password, passwordLessOtp, captchaResponse: '', tfaPasscode, mersOrganization, appKey: getApp(), rememberMe });

    const submitHandler = (e, isAuthenticate) => {
        e.preventDefault();
        if (onFormSubmit) {
            onFormSubmit(getFormValues());
            return;
        }
        if (isAuthenticate || appUserLoginTfa) {
            authenticate(e);
        } else {
            onNextClick();
        }
    };

    const loginWithPasswordClicked = () => {
        setAlert(null);
        setPasswordLoginChosen(true);
        setLoginMode('password');
    };

    const openEulaPage = (e) => {
        e.preventDefault();
        navigate(constants.ROUTE_PAGE_EULA);
    };

    // --- Derived state ---
    const showPasswordLessLogin = loginMode === 'passwordless_otp' || (ERR_REQUIRES_PASSWORD_LESS_OTP && !isPasscodeFlow);
    // The offer screen carries no credential input, so neither the password field nor Login belongs on it.
    const showPasscodeOffer = isPasscodeFlow && !showPasswordLessLogin && !passwordLoginChosen;
    const showPasswordLogin = !showPasscodeOffer && (loginMode === 'password' || ERR_REQUIRES_PASSWORD_VAL || !!onFormSubmit);
    const showContinue = !(remainingAttempts != null && remainingAttempts !== '' && Number(remainingAttempts) === 0);
    const isGlobalIdTransition = errTransition || errNoGlobal;
    const isGlobalIdTransitionExceeded = errAttemptsExceeded;
    const isWebAuthnRegistered = TFA_IS_WEBAUTHN_REGISTERED === 'Y';
    const isTfaDeviceRegistered = TFA_IS_TFA_DEVICE_REGISTERED === 'Y';
    const showOtherTfaOptions = appUserLoginTfa ? (isWebAuthnRegistered || isTfaDeviceRegistered) : true;
    const currentOverride = TFA_USE_OVERRIDE_VAL || '';
    const usePasskey = WEBAUTHN_DELIVERY === currentOverride;
    const isSaml = JspUtil.isValueAvailable(window.jspSamlSession);

    const getLoginType = () => {
        let currLoginApp = jspVariables.jspApp || appKey || '';
        if (currLoginApp === "GLOBAL" && jspVariables.jspLoginApp) {
            currLoginApp = jspVariables.jspLoginApp;
        }
        // The field holds the Global ID email once attempts are exhausted (prefilled)
        // or the user picked "Login with Global ID", and a Global ID is always an
        // email. The target app (appKey) is unchanged — only the credential type
        // differs. During the transition prompt itself the field still shows the app
        // User ID the user typed, so that state keeps its original label.
        if (isGlobalIdTransitionExceeded || useGlobalIdLogin) {
            return 'email';
        }
        // If no app resolved, check jspLoginApp for launcher context
        if (!currLoginApp) {
            currLoginApp = jspVariables.jspLoginApp || '';
        }
        if (!currLoginApp) return 'default';
        const appConfig = loginAppsList.find(app => app.appKey === currLoginApp);
        const isVirtualApp = (() => {
            if (jspVariables.jspIsVirtualLoginApp === 'Y') return true;
            if (jspVariables.jspIsVirtualLoginApp === undefined || jspVariables.jspIsVirtualLoginApp === null || jspVariables.jspIsVirtualLoginApp === '') {
                return appConfig && appConfig.virtual;
            }
            return false;
        })();
        if (currLoginApp !== constants.APP_LAUNCHER &&
            (currLoginApp === "GLOBAL" || (isVirtualApp && (!appConfig || !appConfig.appKey || (appConfig.appKey.toUpperCase() !== 'ANY' && appConfig.appKey.toUpperCase() !== constants.APP_LAUNCHER))))) {
            return 'email';
        }
        if (currLoginApp === constants.APP_LAUNCHER || (appConfig && (appConfig.appKey.toUpperCase() === 'ANY' || appConfig.appKey.toUpperCase() === constants.APP_LAUNCHER))) {
            return 'any';
        }
        return 'default';
    };

    const loginType = getLoginType();
    const emailLogin = loginType === 'email';
    const anyApp = loginType === 'any';

    const getUsernameLabel = () => {
        if (emailLogin) return getLabelHook('label_email');
        if (anyApp) return getLabelHook('label_user_id_or_email');
        return getLabelFromJspVariables('label_userid');
    };

    // --- Render: Action button ---
    const renderActionButton = () => {
        if (showPasscodeOffer) return null;

        if (isGlobalIdTransition || errNoGlobal) {
            return (
                <>
                    {showContinue &&
                        <Button variant="contained" fullWidth
                            onClick={(e) => continueLoginWithAppId(e)}
                            data-automation-id="login_global_id_continue_btn">
                            {getLabelHook('transition_to_global_id_continue')}
                        </Button>
                    }
                    {errTransition ?
                        <Button variant="contained" fullWidth
                            onClick={() => loginWithGlobalId()}
                            data-automation-id="login_global_id_cancel_btn"
                            sx={{ mt: showContinue ? 1 : 0 }}>
                            {getLabelHook('transition_to_global_id_cancel')}
                        </Button>
                        :
                        <Button variant="contained" fullWidth
                            onClick={() => registerGlobalId()}
                            data-automation-id="login_global_id_register_btn"
                            sx={{ mt: showContinue ? 1 : 0 }}>
                            {getLabelHook('transition_to_global_id_register_gid')}
                        </Button>
                    }
                </>
            );
        }

        if (eulaDoc && eulaDoc.eulaUrl) {
            return (
                <Button variant="contained" fullWidth name="loginPageEulaBtn"
                    onClick={(e) => openEulaPage(e)}
                    data-automation-id="login_show_eula_btn">{getLabelFromJspVariables('action_show_eula')}</Button>
            );
        }

        if (onFormSubmit || showPasswordLessLogin || appUserLoginTfa || showPasswordLogin) {
            if (appUserLoginTfa && !tfaPrompt) return null;
            return (
                <Button variant="contained" fullWidth name="loginPageBtn" type="submit"
                    data-automation-id="login_page_submit_btn">
                    {getLabelHook('action_login')}
                </Button>
            );
        }

        return (
            <Button variant="contained" fullWidth type="button" name="loginPageNextBtn"
                onClick={() => onNextClick()}
                data-automation-id="login_next_btn">
                {getLabelHook('action_next')}
            </Button>
        );
    };

    // --- Render: 2FA options panel ---
    const renderTfaOptions = () => {
        if (!ERR_REQUIRES_TFA_VAL) return null;

        if (!showOtherTfaOptions) return null;

        return (
            <Box sx={{ mt: 1 }}>
                {/* Section header with help link */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap', pr: 1 }}>{getLabelFromJspVariables('label_other_2fa_options')}</Typography>
                    <Divider sx={{ flex: 1 }} />
                    <Link component="button" type="button" variant="body2"
                        onClick={() => setShow2faHelp(true)}
                        data-automation-id="login_tfa_help_link"
                        sx={{ whiteSpace: 'nowrap', pl: 1, color: '#00b5e2' }}>
                        2FA Passcode Help
                    </Link>
                </Box>

                {/* 2FA options — show the two non-default options side by side */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    {tfaDeliveryPreference !== EMAIL_DELIVERY &&
                        <Box sx={{ flex: '1 1 50%', minWidth: { xs: '100%', sm: 0 } }}>
                            <Button variant="outlined" fullWidth type="button" name="authenticatorBtn"
                                onClick={() => onTfaDeliveryPreferenceOptionChange(EMAIL_DELIVERY)}
                                data-automation-id="login_tfa_email_btn">
                                {getLabelFromJspVariables('action_send_2fa_via_email')}
                            </Button>
                        </Box>
                    }
                    {tfaDeliveryPreference !== GOOGLE_AUTH_DELIVERY &&
                        <TfaOptionCard
                            label={getLabelHook('label_authenticator')}
                            name="authenticatorBtn"
                            isRegistered={isTfaDeviceRegistered}
                            onUse={() => onTfaDeliveryPreferenceOptionChange(GOOGLE_AUTH_DELIVERY)}
                            registerLabel="Register New Device"
                            onRegister={() => editTfaSettings('REGISTER_DEVICE')}
                            showRegister={!appUserLoginTfa}
                            dataAutomationId="login_tfa_authenticator_btn"
                        />
                    }
                    {tfaDeliveryPreference !== WEBAUTHN_DELIVERY &&
                        <TfaOptionCard
                            label={getLabelFromJspVariables('label_passkey')}
                            name="webauthBtn"
                            isRegistered={isWebAuthnRegistered}
                            onUse={() => onTfaDeliveryPreferenceOptionChange(WEBAUTHN_DELIVERY)}
                            registerLabel="Register Passkey"
                            onRegister={() => editTfaSettings('REGISTER_PASSKEY')}
                            showRegister={!appUserLoginTfa}
                            dataAutomationId="login_tfa_passkey_btn"
                        />
                    }
                </Box>
            </Box>
        );
    };
    const memberRefererDomain = (typeof jspGlobals.jspRefererDomain !== 'undefined' && jspGlobals.jspRefererDomain) ? jspGlobals.jspRefererDomain.toLowerCase() : '';
    const showNyseRegPanel = memberRefererDomain === '.nyse.com';

    return (
        <Box id="loginTemplate" data-automation-id="login_template">
            <TFAHelp open={show2faHelp} onClose={() => setShow2faHelp(false)} />

            <form id="formLogin" data-automation-id="form_login" onSubmit={e => { submitHandler(e, (showPasswordLessLogin || showPasswordLogin)); }}>
                {isSaml && <input type="hidden" name="samlSession" data-automation-id="saml_session" value={jspSamlSession} />}

                {/* Alert area */}
                {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}

                {/* LDAP domain */}
                {ldapDomains.length > 1 && !appUserLoginTfa &&
                    <Dropdown selectedValue={selectedLdapDomain}
                        id="ldap_domain_dropdown"
                        name="ldapDomain"
                        dataAutomationId="ldap_domain_dropdown"
                        label="Domain"
                        labelProperty="description" valueProperty="domain"
                        options={ldapDomainOptions}
                        required
                        updateValue={setSelectedLdapDomain} />
                }

                {/* Username */}
                <TextInput
                    label={getUsernameLabel()}
                    id='username_input' name="user" dataAutomationId="username_input"
                    required
                    autoComplete="username"
                    inputRef={usernameInputRef}
                    value={userName}
                    error={!!errors.username}
                    helperText={errors.username || ''}
                    disabled={!!(isGlobalIdTransition || appUserLoginTfa)}
                    onChange={onUserNameChange} />

                {/* Password or Passwordless OTP input */}
                {showPasswordLessLogin ? (
                    <>
                        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
                            <Box component="legend" sx={{ p: 0, mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                                {getLabelHook('label_password_less_code_field')}
                            </Box>
                            <ReactCodeInput type='password' id='password_input' name='password_input' fields={8}
                                data-automation-id='password_input'
                                autoFocus={true} ref={passwordInputRef} autoComplete='off'
                                onChange={value => onPasswordLessOtpChange(value)} />
                        </Box>
                    </>
                ) : (
                    showPasswordLogin && !appUserLoginTfa &&
                    <PasswordInput id='password_input1' name="password" dataAutomationId="password_input1"
                        label={getLabelHook('label_password')}
                        autoComplete='current-password' autoFocus={true}
                        inputRef={(input) => { passwordInputRef.current = input; }}
                        value={password}
                        error={!!errors.password}
                        helperText={errors.password || ''}
                        required
                        disabled={!!isGlobalIdTransition}
                        onChange={e => onPasswordChange(e)} />
                )}

                {/* TFA prompt + passcode input — above the Login button for every delivery. Passkey shows the prompt only (no OTP input). */}
                {ERR_REQUIRES_TFA_VAL && tfaPrompt &&
                    <Alert severity="success" aria-live="polite" sx={{ mt: 1 }}>{tfaPrompt}</Alert>
                }
                {ERR_REQUIRES_TFA_VAL && !usePasskey &&
                    <TextInput
                        label={getLabelHook('label_tfa_passcode')}
                        name="otpCode"
                        id="otpcode_input"
                        dataAutomationId="otpcode_input"
                        required
                        autoFocus={true}
                        autoComplete="one-time-code"
                        numericOnly maxLength={6}
                        inputRef={tfaPasscodeInputRef}
                        value={tfaPasscode}
                        error={!!errors.tfaPasscode}
                        helperText={errors.tfaPasscode || ''}
                        onChange={e => { if (errors.tfaPasscode) setErrors(prev => ({ ...prev, tfaPasscode: '' })); onTfaPasscodeChange(e); }}
                    />
                }

                {/* Remember me */}
                {!showPasswordLessLogin && jspVariables.showRememberMe && !appUserLoginTfa &&
                    <FormControlLabel
                        control={<Checkbox checked={rememberMe} onChange={e => onRememberUserNameChange(e)} size="small" name="rememberme" data-automation-id="rememberme" />}
                        label={getLabelHook('remember_user_name')}
                        style={{ paddingLeft: "0px" }}
                    />
                }

                {/* Main action button */}
                {renderActionButton()}

                {/* Login with password link (when in passwordless mode) */}
                {showPasswordLessLogin &&
                    <Button variant="contained" fullWidth type="button"
                        name="loginWithPassword"
                        onClick={() => loginWithPasswordClicked()}
                        data-automation-id="login_with_password_btn"
                        sx={{ mt: 1 }}>
                        {getLabelHook('login_with_password')}
                    </Button>
                }

                {/* Passcode offer — the code is emailed only when the user asks for it */}
                {showPasscodeOffer &&
                    <Collapse in appear timeout={250}>
                        <Button variant="contained" fullWidth type="button"
                            name="sendOneTimePasscode"
                            disabled={sendingPasscode}
                            onClick={() => onSendOneTimePasscodeClick()}
                            data-automation-id="login_send_one_time_passcode_btn"
                            sx={{ mt: 1 }}>
                            {getLabelHook('action_send_one_time_passcode')}
                        </Button>
                        <Button variant="contained" fullWidth type="button"
                            name="loginWithPassword"
                            onClick={() => loginWithPasswordClicked()}
                            data-automation-id="login_with_password_btn"
                            sx={{ mt: 1 }}>
                            {getLabelHook('login_with_password')}
                        </Button>
                    </Collapse>
                }

                {/* Company Single Sign-On */}
                {samlUrl &&
                    <Button variant="contained" fullWidth
                        name="companySingleSignOn"
                        onClick={(e) => redirectToSaml(e, samlUrl)}
                        data-automation-id="login_company_sso_btn"
                        sx={{
                            mt: 1,
                            backgroundColor: '#e2e2e2',
                            color: '#000',
                            border: 0,
                            '&:hover': { backgroundColor: '#d4d4d4', border: 0 },
                        }}>
                        {getLabelFromJspVariables('title_company_sso')}
                    </Button>
                }

                {/* Change Password / Forgot Password links */}
                {!appUserLoginTfa &&
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                          <Link component={passwordUrl ? "a" : "button"} type={passwordUrl ? undefined : "button"}
                                  href={passwordUrl || undefined} id="loginPageChangePassword" name="loginPageChangePassword" underline="always" sx={{ ...linkSx, display: 'inline-block' }}
                                onClick={passwordUrl ? undefined : () => navigate(constants.ROUTE_PAGE_CHANGE_PASSWORD)}
                                data-automation-id="login_change_password_btn">
                                {getLabelHook('change_password')}
                        </Link>
                          <Link component={passwordUrl ? "a" : "button"} type={passwordUrl ? undefined : "button"}
                              href={passwordUrl || undefined} id="loginPageForgotPassword" name="loginPageForgotPassword" underline="always" sx={{ ...linkSx, display: 'inline-block' }}
                            onClick={passwordUrl ? undefined : () => navigate(constants.ROUTE_PAGE_FORGOT_PASSWORD)}
                            data-automation-id="login_forgot_password_btn">
                            {getLabelHook('label_forgot_password')}
                        </Link>
                    </Box>
                }

                {showNyseRegPanel &&
                    <div id="nyseRegPanel" data-automation-id="nyse_reg_panel" className="nyse-reg-panel">
                        <hr className="nyse-reg-divider" />
                        <p className="nyse-reg-heading">Don't have an account? Register for an application:</p>
                        <ul className="nyse-reg-list">
                            <li className="nyse-reg-card">
                                <span className="nyse-reg-dot" style={{ backgroundColor: '#1E5FBF' }}></span>
                                <span className="nyse-reg-label">NYSE Connect</span>
                                <a id="nyseConnectReg" data-automation-id="nyse_connect_reg" className="nyse-reg-link"
                                   href={jspGlobals.jspNyseConnectUrl}
                                   target="_blank" rel="noopener noreferrer">Register &rarr;</a>
                            </li>
                            <li className="nyse-reg-card">
                                <span className="nyse-reg-dot" style={{ backgroundColor: '#2E8B57' }}></span>
                                <span className="nyse-reg-label">Listing Manager</span>
                                <a id="listingMgrReg" data-automation-id="listing_mgr_reg" className="nyse-reg-link"
                                   href={jspGlobals.jspNyseListingMgrEmail ? `mailto:${encodeURIComponent(jspGlobals.jspNyseListingMgrEmail)}` : undefined}>Register &rarr;</a>
                            </li>
                            <li className="nyse-reg-card">
                                <span className="nyse-reg-dot" style={{ backgroundColor: '#D08A1E' }}></span>
                                <span className="nyse-reg-label">Data Services Dashboard</span>
                                <a id="dsdReg" data-automation-id="dsd_reg" className="nyse-reg-link"
                                   href={`${window.location.origin}/index.html#/registration?appKey=${jspGlobals.jspNyseDsdAppKey || 'DSD'}`}
                                   target="_blank" rel="noopener noreferrer">Register &rarr;</a>
                            </li>
                        </ul>
                    </div>
                }

                {/* 2FA options panel */}
                {renderTfaOptions()}

                {/* App selector */}
                {showAppSelector &&
                    <Dropdown selectedValue={appKey}
                        id='appkey_dropdown'
                        name='appKey'
                        dataAutomationId='appkey_dropdown'
                        label={getLabelHook('label_application')}
                        labelProperty="description" valueProperty="appKey"
                        options={loginAppsList}
                        updateValue={val => onAppNameChange(val)} />
                }
            </form>
        </Box>
    );
};

export default LoginTemplate;
